// Il cablaggio dei pacchetti condivisi di Integrations (WatchPOD e Flow) nella plancia di
// PopTown. I componenti del pacchetto sono neutri: qui prendono i colori dai token del tema, così
// seguono il tema chiaro e quello scuro.
import type { CSSProperties } from 'react';
import {
  reloadOntoNewVersion,
  waitForRestart,
  type BrowserUpdateOptions,
} from '@integrations/watchpod/browser';
import type { UiProps, UpdateActions } from '@integrations/watchpod/react';
import type { FormSlot } from '@integrations/memo-project/react';
import { api } from './api';

/** Un prefisso solo per tutte le chiavi in storage del client WatchPOD. */
export const STORAGE_PREFIX = 'poptown.watchpod';

/** Dove il browser posta i suoi errori: la rotta dell'host che li inoltra a Flow. */
export const SUPPORT_ERROR_ENDPOINT = '/api/support/error';

/**
 * Durante un aggiornamento il vecchio container risponde fino all'ultimo: si ricarica solo
 * quando `/api/version` dice la versione NUOVA, dopo aver svuotato la cache.
 */
export const browserOptions: BrowserUpdateOptions = {
  healthUrl: '/api/version',
  flushCacheUrl: '/api/updates/flush-cache',
  storagePrefix: STORAGE_PREFIX,
};

export const updateActions: UpdateActions = {
  install: (version) => api.installUpdate(version),
  waitForRestart: (version, onTick) =>
    waitForRestart(browserOptions, version, __APP_VERSION__, onTick),
  reload: () => reloadOntoNewVersion(browserOptions),
  snooze: (version) => api.snoozeUpdate(version),
};

const line = '1px solid var(--border)';

export const updatesUi: UiProps = {
  appName: 'PopTown',
  styles: {
    overlay: { zIndex: 400, background: 'var(--overlay)', backdropFilter: 'blur(2px)' },
    modal: {
      background: 'var(--panel)',
      color: 'var(--text)',
      border: line,
      borderRadius: 'var(--r-2xl)',
      boxShadow: 'var(--shadow-3)',
    },
    title: { font: '600 18px/24px var(--font-ui)', color: 'var(--text)' },
    body: { color: 'var(--text)' },
    notes: { border: line, background: 'var(--bg-2)', borderRadius: 'var(--r-lg)' },
    footer: { borderTop: line },
    button: {
      border: line,
      color: 'var(--text)',
      background: 'var(--panel)',
      borderRadius: 'var(--r-md)',
    },
    buttonPrimary: {
      background: 'var(--accent)',
      color: 'var(--accent-ink)',
      borderRadius: 'var(--r-md)',
    },
    input: { background: 'var(--panel)', color: 'var(--text)', border: line },
    muted: { color: 'var(--dim)' },
    error: { color: 'var(--danger)' },
    section: {
      padding: 20,
      border: line,
      borderRadius: 'var(--r-xl)',
      background: 'var(--panel)',
      color: 'var(--text)',
    },
    sectionTitle: { color: 'var(--text)' },
    row: { borderTop: line },
    rowTitle: { color: 'var(--text)' },
    select: { background: 'var(--panel)', color: 'var(--text)', border: line },
    toggle: { background: 'var(--border-strong)' },
    toggleOn: { background: 'var(--accent)' },
    toast: { background: 'var(--panel)', color: 'var(--text)', border: line },
  },
};

const field: CSSProperties = {
  background: 'var(--panel)',
  border: line,
  color: 'var(--text)',
  borderRadius: 'var(--r-sm)',
};

/** Il modulo «Collegamento a Flow» con i colori di PopTown al posto dei suoi (bianco e nero). */
export const connectionFormStyles: Partial<Record<FormSlot, CSSProperties>> = {
  card: {
    background: 'var(--panel)',
    border: line,
    borderRadius: 'var(--r-xl)',
    color: 'var(--text)',
    padding: 20,
  },
  title: { borderBottom: line, color: 'var(--text)' },
  label: { color: 'var(--dim)' },
  input: field,
  hint: { color: 'var(--dim)', opacity: 1 },
  button: { ...field, borderRadius: 'var(--r-md)' },
  buttonPrimary: {
    background: 'var(--accent)',
    color: 'var(--accent-ink)',
    border: 'none',
    borderRadius: 'var(--r-md)',
  },
  status: { color: 'var(--text)' },
  menu: { background: 'var(--panel)', color: 'var(--text)', border: line },
  option: { color: 'var(--text)' },
};
